import {updateUrl} from '@github-ui/history'
import {searchUrl} from '@github-ui/issue-url-helper'
import {noop} from '@github-ui/noop'
import {useLocation, useParams} from '@github-ui/react-router'
import {useAnalytics} from '@github-ui/use-analytics'
import {Button} from '@primer/react'
import {useCallback} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {BUTTON_LABELS} from '../../constants/buttons'
import {ASSIGNED_TO_ME_VIEW, CUSTOM_VIEW} from '../../constants/view-constants'
import {
  useDirtySearchQuery,
  useQueryEditState,
  useQueryMutations,
  useQueryViewEditFields,
} from '../../contexts/QueryContext'
import {useAppNavigate} from '../../hooks/use-app-navigate'
import {commitRemoveUserViewMutation} from '../../mutations/remove-user-view-mutation'
import {isDirty} from '../../utils/type-predicates'
import {isCreatedByAppIssuePathForRepo, isCustomViewIssuePathForRepo} from '../../utils/urls'
import type {DashboardEditViewActionsFragment$key} from './__generated__/DashboardEditViewActionsFragment.graphql'
import styles from './DashboardEditViewActions.module.css'

type DashboardEditViewActionsProps = {
  currentView: DashboardEditViewActionsFragment$key
}

export function DashboardEditViewActions({currentView}: DashboardEditViewActionsProps) {
  const {
    id: viewId,
    color: viewColor,
    name: viewName,
    description: viewDescription,
    icon: viewIcon,
    scopingRepository,
    query,
  } = useFragment<DashboardEditViewActionsFragment$key>(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment DashboardEditViewActionsFragment on Shortcutable {
        id
        name
        description
        icon
        color
        query
        scopingRepository {
          name
          owner {
            login
          }
        }
      }
    `,
    currentView,
  )

  const relayEnvironment = useRelayEnvironment()
  const {pathname} = useLocation()
  const {navigateToView} = useAppNavigate()

  const {setIsEditing, setIsNewView, dirtyViewId, isNewView} = useQueryEditState()

  const {dirtyTitle, dirtyDescription, dirtyViewIcon, dirtyViewColor} = useQueryViewEditFields()
  const dirtySearchQuery = useDirtySearchQuery()
  const {commitUserViewEdit, clearSavedViewEditState} = useQueryMutations()

  const {sendAnalyticsEvent} = useAnalytics()

  const {author, assignee, mentioned} = useParams<{author: string; assignee: string; mentioned: string}>()

  const customViewQuery = `${CUSTOM_VIEW.defaultQuery} ${CUSTOM_VIEW.query({
    author,
    assignee,
    mentioned,
    createdByApp: isCreatedByAppIssuePathForRepo(pathname),
  })}`

  const viewQuery = isCustomViewIssuePathForRepo(pathname) ? customViewQuery : query

  const originalQuery = scopingRepository
    ? `repo:${scopingRepository.owner.login}/${scopingRepository.name} ${viewQuery}`
    : `${viewQuery}`

  const onUpdateViewQuery = useCallback(() => {
    // In a future world, we should consider using a true edit / create form that manages this for us.
    const updatedViewState = {
      viewName: isDirty(dirtyTitle) ? dirtyTitle : viewName,
      viewDescription: isDirty(dirtyDescription) ? dirtyDescription : viewDescription,
      viewIcon: isDirty(dirtyViewIcon) ? dirtyViewIcon : viewIcon,
      viewColor: isDirty(dirtyViewColor) ? dirtyViewColor : viewColor,
      viewQuery: isDirty(dirtySearchQuery) ? dirtySearchQuery : originalQuery,
    }

    if (updatedViewState.viewName.trim() !== '') {
      sendAnalyticsEvent('search.save', 'FILTER_BAR_SAVE_BUTTON', {
        new_color: updatedViewState.viewColor,
        new_icon: updatedViewState.viewIcon,
        new_query: updatedViewState.viewQuery,
        new_view_description: updatedViewState.viewDescription,
        new_view_name: updatedViewState.viewName,
        prev_color: viewColor,
        prev_icon: viewIcon,
        prev_query: originalQuery,
        prev_view_description: viewDescription,
        prev_view_name: viewName,
      })

      const args = {
        viewId,
        ...updatedViewState,
        onSuccess: () => {
          const url = searchUrl({viewId})
          updateUrl(url)
          setIsEditing(false)
        },
        relayEnvironment,
      }

      commitUserViewEdit(args)

      setIsNewView(false)
      clearSavedViewEditState()
    }
  }, [
    dirtyTitle,
    sendAnalyticsEvent,
    dirtyViewColor,
    dirtyViewIcon,
    dirtySearchQuery,
    dirtyDescription,
    viewColor,
    viewIcon,
    originalQuery,
    viewDescription,
    viewName,
    viewId,
    relayEnvironment,
    commitUserViewEdit,
    setIsNewView,
    clearSavedViewEditState,
    setIsEditing,
  ])

  const deleteDirtyView = useCallback(() => {
    if (dirtyViewId === undefined) {
      return
    }

    const payload = {
      environment: relayEnvironment,
      input: {shortcutId: dirtyViewId},
      onError: () => noop,
      onCompleted: () => {
        navigateToView({viewId: ASSIGNED_TO_ME_VIEW.id, canEditView: true})
      },
    }

    commitRemoveUserViewMutation(payload)
  }, [dirtyViewId, navigateToView, relayEnvironment])

  const onCancel = useCallback(() => {
    setIsEditing(false)

    if (isNewView) {
      deleteDirtyView()
    }

    setIsNewView(false)
    clearSavedViewEditState()

    navigateToView({viewId, canEditView: true})
  }, [setIsEditing, isNewView, setIsNewView, clearSavedViewEditState, navigateToView, viewId, deleteDirtyView])

  return (
    <div className={`${styles.gap8} d-flex flex-row`}>
      <Button onClick={onCancel}>{BUTTON_LABELS.cancel}</Button>
      <Button variant="primary" onClick={onUpdateViewQuery}>
        {BUTTON_LABELS.saveView}
      </Button>
    </div>
  )
}
